// The map types the Explore map offers, in the order they are listed in the picker.
//
// Three, not a menu of everything Mapbox publishes. Google's own layers sheet is the shape this
// follows: a default, a photo one, and a terrain one. Past that a visitor is choosing between
// styles that answer the same question, and the picker becomes something to read rather than
// something to tap.

export type ExploreMapStyleId = 'monochrome' | 'satellite' | 'outdoors';

export interface ExploreMapStyle {
  id: ExploreMapStyleId;
  /** What the picker calls it. */
  label: string;
  /** One line under the label saying what it is for. */
  hint: string;
  /**
   * The Mapbox account the style belongs to, and the style's own id.
   *
   * Held apart rather than as one `mapbox://` string because the two things that need them want
   * them in different shapes: the map takes `mapbox://styles/{owner}/{styleId}`, and the Static
   * Images API that draws the picker's thumbnails takes them as two path segments.
   */
  owner: string;
  styleId: string;
  /**
   * Whether the ground this style draws is light.
   *
   * The course name labels are painted over it, so they have to flip: near-white on a dark
   * ground, near-black on a light one. Everything else on the map (the gold pins, the gold
   * clusters) carries its own dark outline and reads on either.
   */
  isLightGround: boolean;
}

export const EXPLORE_MAP_STYLES: ExploreMapStyle[] = [
  {
    id: 'monochrome',
    label: 'Monochrome',
    hint: 'The GolfPlayed map',
    // Brand "Monochrome" style (account: golfplayed, published 2024-05-29). Dark ground —
    // background is hsl(0, 0%, 16%). The token in .env.local must belong to the same golfplayed
    // account: custom styles are account-scoped, and a foreign token renders a blank globe.
    owner: 'golfplayed',
    styleId: 'clws4surt011b01qx6qk794sk',
    isLightGround: false,
  },
  {
    id: 'satellite',
    label: 'Satellite',
    hint: 'Photos, with road names',
    // satellite-streets, not plain satellite: the photo on its own has no place names on it, so
    // a course pin sits in an unlabelled field and there is no way to tell which town it is in.
    owner: 'mapbox',
    styleId: 'satellite-streets-v12',
    isLightGround: false,
  },
  {
    id: 'outdoors',
    label: 'Outdoors',
    hint: 'Terrain and contours',
    owner: 'mapbox',
    styleId: 'outdoors-v12',
    isLightGround: true,
  },
];

export const mapStyleUrl = (style: ExploreMapStyle): string =>
  `mapbox://styles/${style.owner}/${style.styleId}`;

// The picker's thumbnails: the same patch of ground drawn three times, once per style, so the
// three tiles differ only in the thing the visitor is choosing between.
//
// St Andrews, because it is the one place in golf everybody recognises, and because it happens to
// be the right kind of subject: coast, town, fairway and contour all in one frame, so each style
// has something of its own to show. A landlocked suburb would give three tiles of grey streets.
//
// Drawn by the Mapbox Static Images API rather than shipped as three PNGs in /public. A PNG would
// go stale the moment the brand style is edited in Studio, and would have to be redrawn by hand
// for every style added here; this is always the live style. @2x so it is sharp on a phone.
//
// logo/attribution off because these are 80px tiles and the Mapbox wordmark would fill one. The
// page carries Mapbox's attribution on the map itself (see the AttributionControl in Explore), so
// it stays credited, which is what the terms ask for.
// lon,lat,zoom,bearing,pitch. Flat, not tilted: these are three samples of a style, and a
// pitched one reads as a view of a place instead.
//
// The size is the size the tile is actually drawn at (the sheet caps its row at max-w-md, so a
// tile is about 130px wide). Asking for a much larger image and shrinking it would be fine for a
// photo and is wrong for a map: the labels are baked in at the requested scale, so an oversized
// request comes back with oversized place names.
// Zoom 13 is the level that gives all three tiles something: any further out and the frame is
// mostly North Sea, any further in and the brand style (which draws very little at street level)
// is a black square.
const THUMBNAIL_CENTRE = '-2.8034,56.3433,13,0,0';
const THUMBNAIL_SIZE = '160x120';

export const mapStyleThumbnailUrl = (
  style: ExploreMapStyle,
  accessToken: string,
): string =>
  `https://api.mapbox.com/styles/v1/${style.owner}/${style.styleId}/static/${THUMBNAIL_CENTRE}/${THUMBNAIL_SIZE}@2x?access_token=${accessToken}&logo=false&attribution=false`;

export const DEFAULT_MAP_STYLE_ID: ExploreMapStyleId = 'monochrome';

export const getExploreMapStyle = (id: ExploreMapStyleId): ExploreMapStyle =>
  EXPLORE_MAP_STYLES.find((style) => style.id === id) ?? EXPLORE_MAP_STYLES[0];

// Remembered per browser, not per member. Explore is public, so there is no account to hang it
// on for most of the people who will use it, and a visitor who picks Satellite means it for the
// next visit too.
const STORAGE_KEY = 'explore.mapStyle';

export const readStoredMapStyleId = (): ExploreMapStyleId => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (EXPLORE_MAP_STYLES.some((style) => style.id === stored)) {
      return stored as ExploreMapStyleId;
    }
  } catch {
    // Private mode and blocked storage both throw on read. The map still has to open, so a
    // failure here just means the brand style, same as a first visit.
  }
  return DEFAULT_MAP_STYLE_ID;
};

export const storeMapStyleId = (id: ExploreMapStyleId): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Same as above: not remembering the choice is not a reason to break the tap that made it.
  }
};
